'use client';

import React from 'react';
import Link from 'next/link';
import {
  ArrowRight,
  Lock,
  Sparkles,
  Zap,
  ShieldCheck,
  RefreshCw,
  Check,
} from 'lucide-react';

export default function PricingSection() {
  return (
    <section
      id="pricing"
      className="relative overflow-hidden border-t border-white/10 bg-[#050505] px-6 py-16 text-white sm:px-8 lg:px-12"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-20 h-[320px] w-[320px] -translate-x-1/2 rounded-full bg-[#f4c542]/10 blur-3xl" />
        <div className="absolute right-0 top-1/2 h-[300px] w-[300px] rounded-full bg-[#f4c542]/[0.06] blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl">
        <div className="mx-auto max-w-4xl text-center">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#f4c542]">
            FHITZ Pricing
          </p>

          <h2 className="text-4xl font-semibold leading-[0.95] tracking-tight text-white sm:text-5xl md:text-[54px]">
            Your fit is one click away.
          </h2>

          <p className="mt-3 text-base text-white/55 sm:text-lg">
            Choose how you want FHITZ to build your wardrobe.
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-7 gap-y-2 text-sm text-white/72">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-[#f4c542]" />
              <span>AI-powered outfit recommendations</span>
            </div>

            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-[#f4c542]" />
              <span>Account required to track your FHITZ drops</span>
            </div>

            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-[#f4c542]" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <div className="group relative rounded-[28px] border border-white/10 bg-[#090909] p-6 shadow-[0_0_0_1px_rgba(255,255,255,0.02)] transition duration-300 hover:border-white/20 sm:p-7">
            <div className="flex min-h-[220px] flex-col">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5">
                <ShieldCheck className="h-5 w-5 text-white/80" />
              </div>

              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-3xl font-semibold tracking-tight text-white">
                    Free
                  </h3>
                  <p className="mt-2 max-w-md text-base leading-7 text-white/65">
                    Perfect for exploring FHITZ and getting started.
                  </p>
                </div>
              </div>

              <div className="mt-auto flex items-end gap-2 pt-5">
                <span className="text-5xl font-semibold tracking-tight text-white">
                  $0
                </span>
                <span className="pb-1 text-base text-white/60">forever</span>
              </div>
            </div>

            <div className="mt-5 h-px w-full bg-white/10" />

            <div className="mt-5 space-y-2.5">
              <Feature text="2 FHITZ drops per week" />
              <Feature text="3 outfits per drop" />
              <Feature text="Budget slider up to $1,000" />
              <Feature text="Pick outfit pieces you need" />
              <Feature text="Hats and bags accessories" />
              <Feature text="1 free item swap per week" />
              <Feature text="Limited saved outfits" />
              <Feature text="Account required to track your FHITZ drops" />
            </div>

            <div className="mt-7">
              <Link
                href="/early-access"
                className="inline-flex w-full items-center justify-center gap-3 rounded-2xl border border-white/15 bg-transparent px-6 py-3.5 text-base font-medium text-white transition duration-300 hover:border-white/30 hover:bg-white/[0.03]"
              >
                Get Started
                <ArrowRight className="h-4 w-4" />
              </Link>

              <p className="mt-3 text-center text-xs text-white/45">
                Join the waitlist for FHITZ access
              </p>
            </div>
          </div>

          <div className="relative rounded-[28px] border border-[#f4c542]/80 bg-[#080808] p-6 shadow-[0_0_0_1px_rgba(244,197,66,0.08),0_0_60px_rgba(244,197,66,0.08)] sm:p-7">
            <div className="pointer-events-none absolute inset-0 rounded-[28px] bg-[radial-gradient(circle_at_top_right,rgba(244,197,66,0.12),transparent_35%)]" />

            <div className="relative">
              <div className="flex min-h-[220px] flex-col">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-[#f4c542]/20 bg-[#f4c542]/10">
                  <Sparkles className="h-5 w-5 text-[#f4c542]" />
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-3xl font-semibold tracking-tight text-white">
                    Premium
                  </h3>
                  <span className="rounded-full bg-[#f4c542] px-3 py-1 text-xs font-semibold text-black">
                    Most Popular
                  </span>
                </div>

                <p className="mt-2 max-w-md text-base leading-7 text-white/65">
                  Unlock unlimited FHITZ drops and premium tools for your best
                  style.
                </p>

                <div className="mt-auto flex items-end gap-2 pt-5">
                  <span className="text-5xl font-semibold tracking-tight text-white">
                    $X.XX
                  </span>
                  <span className="pb-1 text-base text-white/60">/ month</span>
                </div>
              </div>

              <div className="mt-5 h-px w-full bg-white/10" />

              <div className="mt-5 grid gap-x-5 gap-y-2.5 sm:grid-cols-2">
                <Feature text="Unlimited FHITZ drops" premium />
                <Feature text="3 outfits per drop" premium />
                <Feature text="Unlimited outfit variations" premium />
                <Feature text="Unlimited budget adjustments" premium />
                <Feature text="Outfit budget meter up to $2,000" premium />
                <Feature text="Sneaker budget meter up to $1,500" premium />
                <Feature text="Swap individual pieces" premium />
                <Feature text="Upload inspiration photos" premium />
                <Feature text="Preferred and avoided brands" premium />
                <Feature text="Premium belt accessory" premium />
                <Feature text="Full outfit history" premium />
                <Feature text="More saved outfits" premium />
                <Feature text="Full style profile" premium />
                <Feature text="No ads ever" premium />
                <Feature text="Early access to new features" premium />
              </div>

              <div className="mt-7">
                <Link
                  href="/early-access"
                  className="inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-[#f4c542] px-6 py-3.5 text-base font-semibold text-black transition duration-300 hover:scale-[1.01] hover:bg-[#ffd65a]"
                >
                  Get Unlimited Access
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-white/45">
          <ShieldCheck className="h-4 w-4" />
          <span>Your style. Your choice. Built by FHITZ.</span>
        </div>
      </div>
    </section>
  );
}

function Feature({
  text,
  premium = false,
}: {
  text: string;
  premium?: boolean;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <div
        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
          premium ? 'bg-[#f4c542]/12' : 'bg-white/5'
        }`}
      >
        <Check className="h-3 w-3 text-[#f4c542]" />
      </div>
      <p className="text-sm font-medium leading-5 text-white/82">{text}</p>
    </div>
  );
}